"use client";

import { useEffect, useId, useRef } from "react";

/**
 * A one-time-code field.
 *
 * Hand-written rather than pulled from a component library because the
 * accessibility contract is the whole point, and the usual six-separate-inputs
 * pattern breaks most of it: a screen reader announces six unlabelled boxes,
 * pasting a code fills only the first, and `autocomplete="one-time-code"` on six
 * elements gives iOS nothing coherent to autofill.
 *
 * So this is ONE real input carrying the whole value, with the cells drawn
 * behind it. Autofill, paste, keyboard, screen readers and the Android SMS
 * Retriever all see a single field, which is what they all expect.
 *
 * Two traps this component avoids, both of which have bitten this codebase
 * before:
 *
 *  - The input is never `disabled` while submitting. Disabling a focused field
 *    dismisses the mobile keyboard and drops the caret, so a failed code leaves
 *    the person tapping a dead box. It goes `readOnly` instead, with a spinner.
 *  - The cells are painted with opacity/colour, never gated behind an animated
 *    `opacity: 0`. A starved rAF (which happens in this app's WebView) would
 *    leave a focusable, invisible field.
 *
 * See `plans/phone-auth-launch-audit.md` NATIVE-02.
 */

export type OtpCodeInputProps = {
  value: string;
  onChange: (value: string) => void;
  /** Fired once the field holds a full-length code. */
  onComplete?: (value: string) => void;
  length?: number;
  /** Renders the cells in the error state and marks the field invalid. */
  invalid?: boolean;
  /** Blocks edits without removing focus or dismissing the keyboard. */
  busy?: boolean;
  label?: string;
  /** Announced with the field; keep it short. */
  description?: string;
  /**
   * Keep the label for screen readers but take it out of the layout. For hosts
   * that already carry a heading — repeating "Enter the code" under "OTP
   * Verification" is noise for sighted users and duplication for everyone else.
   */
  visuallyHideLabel?: boolean;
  autoFocus?: boolean;
};

const DIGITS_ONLY = /\D/g;

export function OtpCodeInput({
  value,
  onChange,
  onComplete,
  length = 6,
  invalid = false,
  busy = false,
  label = "Verification code",
  description,
  visuallyHideLabel = false,
  autoFocus = false,
}: OtpCodeInputProps) {
  const inputId = useId();
  const describedById = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!autoFocus) return;
    // Two frames: the drawer's own entrance transition steals focus on the
    // first, so focusing immediately gets silently undone.
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => inputRef.current?.focus());
    });
    return () => cancelAnimationFrame(raf);
  }, [autoFocus]);

  useEffect(() => {
    if (value.length !== length) {
      completedRef.current = null;
      return;
    }
    // Guard against re-firing for the same code, which would double-submit when
    // the parent re-renders while the request is in flight.
    if (completedRef.current === value) return;
    completedRef.current = value;
    onComplete?.(value);
  }, [length, onComplete, value]);

  // Keyed by position rather than by array index. The cells are a fixed-length
  // display of one string, so they never reorder — but a stable, meaningful key
  // says that explicitly instead of relying on the reader to notice.
  const cells = Array.from({ length }, (_, index) => ({
    position: index,
    key: `otp-position-${index}`,
    digit: value[index] ?? "",
  }));
  const activeIndex = Math.min(value.length, length - 1);

  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={inputId}
        className={
          visuallyHideLabel
            ? "sr-only"
            : "text-sm font-medium text-text-secondary"
        }
      >
        {label}
      </label>

      <div className="relative">
        {/* The real field. Transparent, but full-size and on top, so every tap
            lands on it and the caret/selection belongs to a single value. */}
        <input
          ref={inputRef}
          id={inputId}
          // `type="text"` with a numeric inputMode rather than `type="number"`:
          // number inputs strip leading zeros and expose a spinner.
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          // iOS reads this to offer the code from the SMS banner.
          name="one-time-code"
          pattern="\d*"
          maxLength={length}
          value={value}
          readOnly={busy}
          aria-invalid={invalid}
          aria-describedby={description ? describedById : undefined}
          aria-busy={busy}
          onChange={(event) => {
            // Handles paste for free: a pasted "Your code is 123456" reduces to
            // its digits and is truncated to length.
            onChange(
              event.target.value.replace(DIGITS_ONLY, "").slice(0, length),
            );
          }}
          onKeyDown={(event) => {
            // Backspace at the start of an empty field would otherwise trigger
            // the drawer's own back handler mid-entry.
            if (event.key === "Backspace" && value.length === 0) {
              event.stopPropagation();
            }
          }}
          className="absolute inset-0 z-10 h-full w-full bg-transparent text-transparent caret-transparent outline-none"
          style={{
            // Keep the OS text-selection UI off the invisible field.
            WebkitTapHighlightColor: "transparent",
          }}
        />

        <div className="flex gap-2.5" aria-hidden="true">
          {cells.map(({ position, key, digit }) => {
            const isActive = !busy && position === activeIndex;
            return (
              <div
                key={key}
                className={[
                  // Square-ish and generous: this is the only thing on screen to
                  // aim at, and it comfortably clears the 44px touch minimum.
                  "flex aspect-square flex-1 items-center justify-center rounded-2xl border font-semibold text-2xl tabular-nums transition-colors",
                  invalid
                    ? "border-destructive text-destructive"
                    : isActive
                      ? // The cursor. There is no real caret to see — the input
                        // above is transparent — so the active cell's border is
                        // the only cue about where the next digit lands.
                        "border-text-primary text-text-primary dark:border-white"
                      : "border-border text-text-primary dark:border-white/[0.18]",
                  "bg-transparent",
                  busy ? "opacity-60" : "",
                ].join(" ")}
              >
                {/* Empty rather than a dot placeholder: six dots read as six
                    filled cells at a glance, which is the opposite of the
                    signal somebody needs while typing. */}
                {digit}
              </div>
            );
          })}
        </div>
      </div>

      {description ? (
        <p
          id={describedById}
          // `polite`, not `assertive`: an expiry countdown that interrupts on
          // every tick makes the field unusable with a screen reader.
          aria-live="polite"
          className={
            invalid
              ? "text-sm text-destructive"
              : "text-sm text-text-tertiary dark:text-white/55"
          }
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}
